import React from 'react'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import {
  ButtonAction,
  CustomAction,
} from 'Components/SharedComponents/Surveys/types'
import { orgLeaderPathRoles, NeedsAssessmentWizardStep } from '../../constants'
import { HandleAnyAction } from '../../types'
import { sendStepTransitionEvent } from '../../utils'

type WhatIsYourPrimaryFunctionProps = {
  onAnyAction: HandleAnyAction
  onSetUserPrimaryFunction: (role: UserSurveyPrimaryFunction) => void
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole?: UserSurveyRole
}

export const WhatIsYourPrimaryFunction = ({
  onAnyAction,
  onSetUserPrimaryFunction,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: WhatIsYourPrimaryFunctionProps) => {
  const { nonprofitOrgConfig, userConfig } = useAppState()
  const userActions: CustomAction[] = [
    {
      callback: () =>
        onSetUserPrimaryFunction(UserSurveyPrimaryFunction.Executive),
      checkValue: userPrimaryFunction,
      label: 'Executive',
      type: ActionElementType.RADIO,
      value: UserSurveyPrimaryFunction.Executive,
    },
    {
      callback: () =>
        onSetUserPrimaryFunction(UserSurveyPrimaryFunction.Programming),
      checkValue: userPrimaryFunction,
      label: 'Programming',
      type: ActionElementType.RADIO,
      value: UserSurveyPrimaryFunction.Programming,
    },
    {
      callback: () =>
        onSetUserPrimaryFunction(UserSurveyPrimaryFunction.Fundraising),
      checkValue: userPrimaryFunction,
      label: 'Fundraising',
      type: ActionElementType.RADIO,
      value: UserSurveyPrimaryFunction.Fundraising,
    },
    {
      callback: () =>
        onSetUserPrimaryFunction(UserSurveyPrimaryFunction.FinanceOperations),
      checkValue: userPrimaryFunction,
      label: 'Finance & Operations',
      type: ActionElementType.RADIO,
      value: UserSurveyPrimaryFunction.FinanceOperations,
    },
    {
      callback: () =>
        onSetUserPrimaryFunction(
          UserSurveyPrimaryFunction.MarketingCommunications
        ),
      checkValue: userPrimaryFunction,
      label: 'Marketing & Communications',
      type: ActionElementType.RADIO,
      value: UserSurveyPrimaryFunction.MarketingCommunications,
    },
    {
      callback: () =>
        onSetUserPrimaryFunction(UserSurveyPrimaryFunction.MultipleFunctions),
      checkValue: userPrimaryFunction,
      label: 'Multiple Functions',
      type: ActionElementType.RADIO,
      value: UserSurveyPrimaryFunction.MultipleFunctions,
    },
    {
      callback: () => onSetUserPrimaryFunction(UserSurveyPrimaryFunction.Other),
      checkValue: userPrimaryFunction,
      label: 'Other',
      type: ActionElementType.RADIO,
      value: UserSurveyPrimaryFunction.Other,
    },
  ]

  const nextStep =
    userRole && orgLeaderPathRoles.includes(userRole)
      ? NeedsAssessmentWizardStep.BASELINE_CONFIDENCE
      : NeedsAssessmentWizardStep.INVITE_YOUR_ORG_LEADER

  const nextActions: ButtonAction[] = [
    {
      disabled: !userRole || !userPrimaryFunction,
      label: 'Next',
      path: nextStep,
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: () => {
        sendStepTransitionEvent({
          currentStep: NeedsAssessmentWizardStep.WHAT_IS_YOUR_PRIMARY_FUNCTION,
          nextStep,
          userId: userConfig.id,
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          funderOrgId: nonprofitOrgConfig.funderOrgId,
          userEmail: userConfig.email,
          userPrimaryFunction,
          userJourneyLength,
          userRole,
        })
      },
    },
  ]

  return (
    <StepContainer>
      <Title>What is your primary function within your organization?</Title>
      <UserActions onAnyAction={onAnyAction} userActions={userActions} />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
